<template>
  <div class="filter-card procedure-filter-card">
    <div class="filter-card-header">
      <div class="filter-card-title">
        <i class="fa fa-medkit filter-icon"></i>
        <span>Procedure Filter</span>
      </div>
      <b-dropdown variant="link" size="sm" no-caret right class="filter-menu">
        <template #button-content>
          <i class="fa fa-ellipsis-v text-muted"></i>
        </template>
        <b-dropdown-item @click="$emit('duplicate')">
          <i class="fa fa-copy mr-2"></i>Duplicate
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="$emit('remove')">
          <i class="fa fa-trash mr-2"></i>Remove
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="filter-card-body">
      <!-- Mode: Has had / Has NOT had -->
      <div class="mb-3">
        <div class="filter-radio-group">
          <label class="filter-radio-option">
            <input type="radio" value="has" v-model="localFilter.mode" @change="emitUpdate" />
            <span>Has had procedure</span>
          </label>
          <label class="filter-radio-option">
            <input type="radio" value="none" v-model="localFilter.mode" @change="emitUpdate" />
            <span>Has NOT had procedure</span>
          </label>
        </div>
      </div>

      <!-- Selection Mode -->
      <div class="mb-3">
        <div class="filter-radio-group">
          <label class="filter-radio-option">
            <input type="radio" value="specific" v-model="localFilter.selectionMode" @change="onSelectionModeChange" />
            <span>Specific procedure codes</span>
          </label>
          <label class="filter-radio-option">
            <input type="radio" value="any" v-model="localFilter.selectionMode" @change="onSelectionModeChange" />
            <span>Any procedure</span>
          </label>
        </div>
      </div>

      <!-- Procedure Code Multiselect (only when specific) -->
      <div v-if="localFilter.selectionMode === 'specific'" class="mb-3">
        <label class="filter-label">Procedure Codes <span class="required">*</span></label>
        <multiselect
          v-model="selectedProcCodes"
          :options="procCodeOptions"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="Search procedure codes..."
          class="filter-multiselect"
          @input="onProcCodesChange"
        />
      </div>

      <!-- Status + Timeframe Row -->
      <b-row>
        <b-col md="6">
          <div class="mb-3">
            <label class="filter-label">Status <span class="required">*</span></label>
            <multiselect
              v-model="selectedStatuses"
              :options="statusOptions"
              :multiple="true"
              :searchable="false"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Select status..."
              class="filter-multiselect"
              @input="onStatusesChange"
            />
          </div>
        </b-col>
        <b-col md="6">
          <div class="mb-3">
            <label class="filter-label">Timeframe</label>
            <div class="filter-radio-group mb-2">
              <label class="filter-radio-option">
                <input type="radio" value="none" v-model="timeframeMode" @change="onTimeframeModeChange" />
                <span>Any time (ever)</span>
              </label>
              <label class="filter-radio-option">
                <input type="radio" value="relative" v-model="timeframeMode" @change="onTimeframeModeChange" />
                <span>In the last</span>
              </label>
              <label class="filter-radio-option">
                <input type="radio" value="custom" v-model="timeframeMode" @change="onTimeframeModeChange" />
                <span>Custom range</span>
              </label>
            </div>
            <div v-if="timeframeMode === 'relative'" class="timeframe-row">
              <input
                v-model.number="localFilter.timeframe.value"
                type="number"
                min="1"
                class="filter-form-input timeframe-number"
                @input="emitUpdate"
              />
              <select
                v-model="localFilter.timeframe.unit"
                class="filter-form-input timeframe-unit"
                @change="emitUpdate"
              >
                <option value="months">Months</option>
                <option value="days">Days</option>
                <option value="years">Years</option>
              </select>
            </div>
            <div v-else-if="timeframeMode === 'custom'" class="timeframe-custom-range">
              <div class="d-flex align-items-center gap-2">
                <label class="timeframe-date-label">From</label>
                <input
                  type="date"
                  v-model="localFilter.timeframe.from"
                  :min="minCustomDate"
                  :max="localFilter.timeframe.to || todayStr"
                  class="filter-form-input"
                  @input="emitUpdate"
                />
                <label class="timeframe-date-label">To</label>
                <input
                  type="date"
                  v-model="localFilter.timeframe.to"
                  :min="localFilter.timeframe.from || minCustomDate"
                  :max="todayStr"
                  class="filter-form-input"
                  @input="emitUpdate"
                />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Fee (optional, collapsible) -->
      <div v-if="!showFee" class="mb-2">
        <a href="#" class="add-fee-link" @click.prevent="showFeeFilter">
          <i class="fa fa-plus mr-1"></i>Add fee filter
        </a>
      </div>
      <div v-else class="fee-row mb-2">
        <label class="filter-label">Fee</label>
        <div class="d-flex align-items-center gap-2">
          <select
            v-model="localFilter.fee.operator"
            class="filter-form-input fee-operator"
            @change="emitUpdate"
          >
            <option value="gt">Greater than</option>
            <option value="lt">Less than</option>
          </select>
          <div class="filter-input-group fee-amount">
            <span class="filter-input-prepend">$</span>
            <input
              v-model.number="localFilter.fee.amount"
              type="number"
              min="0"
              step="0.01"
              class="filter-form-input filter-input-prepended"
              @input="emitUpdate"
            />
          </div>
          <a href="#" class="text-muted ml-2" @click.prevent="removeFee" title="Remove fee filter">
            <i class="fa fa-times"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'ProcedureFilterCard',
  components: { Multiselect },
  props: {
    value: {
      type: Object,
      required: true
    },
    procCodeOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFilter: JSON.parse(JSON.stringify(this.value)),
      selectedProcCodes: [],
      selectedStatuses: [],
      showFee: this.value.fee !== null,
      timeframeMode: !this.value.timeframe ? 'none' : (this.value.timeframe?.mode === 'custom' ? 'custom' : 'relative'),
      statusOptions: [
        { value: 2, text: 'Completed' },
        { value: 1, text: 'Treatment Planned' },
        { value: 3, text: 'Existing/Current' },
        { value: 6, text: 'Referred' }
      ]
    };
  },
  computed: {
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    minCustomDate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 4);
      return d.toISOString().slice(0, 10);
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.localFilter = JSON.parse(JSON.stringify(newVal));
        if (!this.localFilter.fee) {
          this.localFilter.fee = { operator: 'gt', amount: 0 };
        }
        this.timeframeMode = !newVal.timeframe ? 'none' : (newVal.timeframe?.mode === 'custom' ? 'custom' : 'relative');
        this.syncSelections();
      },
      deep: true
    }
  },
  created() {
    if (!this.localFilter.mode) {
      this.localFilter.mode = 'has';
    }
    this.syncSelections();
    if (!this.localFilter.fee) {
      this.localFilter.fee = { operator: 'gt', amount: 0 };
    }
  },
  methods: {
    syncSelections() {
      // Sync procCodes array to multiselect objects
      this.selectedProcCodes = (this.localFilter.procCodes || [])
        .map(code => this.procCodeOptions.find(o => o.value === code))
        .filter(Boolean);

      // Sync statuses array to multiselect objects
      this.selectedStatuses = (this.localFilter.statuses || [])
        .map(s => this.statusOptions.find(o => o.value === s))
        .filter(Boolean);
    },
    onTimeframeModeChange() {
      if (this.timeframeMode === 'none') {
        this.localFilter.timeframe = null;
      } else if (this.timeframeMode === 'relative') {
        this.localFilter.timeframe = { mode: 'relative', value: 6, unit: 'months' };
      } else {
        this.localFilter.timeframe = { mode: 'custom', from: '', to: '' };
      }
      this.emitUpdate();
    },
    onSelectionModeChange() {
      if (this.localFilter.selectionMode === 'any') {
        this.localFilter.procCodes = [];
        this.selectedProcCodes = [];
      }
      this.emitUpdate();
    },
    onProcCodesChange(selected) {
      this.localFilter.procCodes = selected.map(s => s.value);
      this.emitUpdate();
    },
    onStatusesChange(selected) {
      this.localFilter.statuses = selected.map(s => s.value);
      this.emitUpdate();
    },
    showFeeFilter() {
      this.showFee = true;
      if (!this.localFilter.fee) {
        this.localFilter.fee = { operator: 'gt', amount: 0 };
      }
    },
    removeFee() {
      this.showFee = false;
      this.localFilter.fee = null;
      this.emitUpdate();
    },
    emitUpdate() {
      const output = { ...this.localFilter };
      if (!this.showFee) {
        output.fee = null;
      }
      this.$emit('input', output);
    }
  }
};
</script>

<style scoped lang="scss">
.procedure-filter-card {
  border-left: 3px solid #547fff;
}

.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: visible;
}

.filter-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #334155;

  .filter-icon {
    color: #547fff;
    font-size: 0.875rem;
  }
}

.filter-menu ::v-deep .btn-link {
  padding: 0.125rem 0.375rem;
  color: #94a3b8;
  &:hover { color: #475569; }
}

.filter-card-body {
  padding: 0.875rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.375rem;
  display: block;

  .required {
    color: #ef4444;
  }
}

.filter-multiselect ::v-deep {
  .multiselect__tags {
    min-height: 34px;
    padding: 4px 40px 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
  }
  .multiselect__tag {
    background: #eef2ff;
    color: #3730a3;
    border-radius: 4px;
    font-size: 0.75rem;
    padding: 2px 22px 2px 8px;
    margin-bottom: 2px;
  }
  .multiselect__tag-icon {
    &:after { color: #6366f1; }
    &:hover { background: #c7d2fe; }
  }
  .multiselect__option--highlight {
    background: #547fff;
  }
  .multiselect__content-wrapper {
    z-index: 1060;
    border-radius: 6px;
    border-color: #e2e8f0;
    max-height: 200px;
  }
  .multiselect__placeholder {
    font-size: 0.8125rem;
    color: #94a3b8;
    padding-top: 0;
    margin-bottom: 0;
  }
}

// Native form input styling (matches Online Scheduling pattern)
.filter-form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #547fff;
    box-shadow: 0 0 0 2px rgba(84, 127, 255, 0.15);
  }
}

select.filter-form-input {
  height: 34px;
  line-height: 1.2;
  background: white;
  cursor: pointer;
}

.filter-radio-group {
  display: flex;
  gap: 16px;
}

.filter-radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0;

  input[type="radio"] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: #547fff;
  }
}

.filter-input-group {
  display: flex;
  align-items: stretch;
}

.filter-input-prepend {
  display: flex;
  align-items: center;
  padding: 0 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-right: none;
  border-radius: 6px 0 0 6px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.filter-input-prepended {
  border-radius: 0 6px 6px 0;
}

.timeframe-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeframe-label {
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
}

.timeframe-number {
  width: 60px;
}

.timeframe-unit {
  width: 100px;
}

.timeframe-custom-range {
  .d-flex {
    gap: 0.5rem;
  }

  input[type="date"] {
    width: 140px;
  }
}

.timeframe-date-label {
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
}

.add-fee-link {
  font-size: 0.8125rem;
  color: #547fff;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

.fee-row {
  .fee-operator {
    width: 140px;
  }
  .fee-amount {
    width: 120px;
  }
  .d-flex {
    gap: 0.5rem;
  }
}
</style>
